/**
 * Container - Game Container Management
 * 
 * Manages the container (floor + walls) geometry and provides
 * sensor logic to determine which packages are "inside" the container.
 */

import { PhysicsWorld, PhysicsBody } from '../engine/PhysicsWorld';

/**
 * Container geometry (all values in pixels)
 */
export interface ContainerGeometry {
    /** Left inner edge of container (inside wall) */
    left: number;
    /** Right inner edge of container (inside wall) */
    right: number;
    /** Top of container (fill line) */
    top: number;
    /** Floor surface Y position */
    floorY: number;
    /** Wall thickness */
    wallThickness: number;
    /** Inner width */
    width: number;
    /** Inner height */
    height: number;
    /** Total container area (inner) */
    area: number;
}

/**
 * Bounding box of a body (for scoring calculations)
 */
export interface BodyBounds {
    body: PhysicsBody;
    x: number;
    y: number;
    minX: number;
    maxX: number;
    minY: number;  // Top (lower Y = higher on screen)
    maxY: number;  // Bottom
    width: number;
    height: number;
    area: number;
}

/**
 * Container class - manages container physics and sensor logic
 */
export class Container {
    private physics: PhysicsWorld;
    private geometry: ContainerGeometry | null = null;
    
    // Static body handles for cleanup
    private floorHandle: number | null = null;
    private leftWallHandle: number | null = null;
    private rightWallHandle: number | null = null;

    constructor(physics: PhysicsWorld) {
        this.physics = physics;
    }

    /**
     * Create container walls and floor.
     * 
     * @param left Left inner edge X
     * @param right Right inner edge X
     * @param top Top of container Y (fill line)
     * @param bottom Bottom Y (where floor top surface is)
     * @param wallThickness Thickness of walls
     */
    create(
        left: number,
        right: number,
        top: number,
        bottom: number,
        wallThickness: number
    ): void {
        // Clean up existing bodies
        this.destroy();

        // Store geometry
        const width = right - left;
        const height = bottom - top;
        
        this.geometry = {
            left,
            right,
            top,
            floorY: bottom,
            wallThickness,
            width,
            height,
            area: width * height,
        };

        // Create floor (full width including walls)
        this.floorHandle = this.physics.createStaticBody({
            x: (left + right) / 2,
            y: bottom + wallThickness / 2,
            shape: {
                type: 'box',
                width: width + wallThickness * 2,
                height: wallThickness,
            },
            friction: 1.0,
            restitution: 0,
        });

        // Create left wall (extends from top to below floor)
        this.leftWallHandle = this.physics.createStaticBody({
            x: left - wallThickness / 2,
            y: (top + bottom) / 2,
            shape: {
                type: 'box',
                width: wallThickness,
                height: height,
            },
            friction: 1.0,
            restitution: 0,
        });

        // Create right wall
        this.rightWallHandle = this.physics.createStaticBody({
            x: right + wallThickness / 2,
            y: (top + bottom) / 2,
            shape: {
                type: 'box',
                width: wallThickness,
                height: height,
            },
            friction: 1.0,
            restitution: 0,
        });
    }

    /**
     * Get container geometry (for rendering and calculations)
     */
    getGeometry(): ContainerGeometry | null {
        return this.geometry;
    }

    /**
     * Check if a body's center is within container bounds.
     * This is a simple check - body center must be inside.
     */
    isBodyInContainer(body: PhysicsBody): boolean {
        if (!this.geometry) return false;

        const pos = this.physics.getPosition(body.handle);
        if (!pos) return false;

        const { left, right, top, floorY } = this.geometry;

        return (
            pos.x > left &&
            pos.x < right &&
            pos.y > top &&
            pos.y < floorY
        );
    }

    /**
     * Get all package bodies that are inside the container.
     */
    getBodiesInContainer(): PhysicsBody[] {
        return this.physics.getAllBodies().filter(body => this.isBodyInContainer(body));
    }

    /**
     * Get body bounds for a physics body.
     * Calculates axis-aligned bounding box based on shape and rotation.
     */
    getBodyBounds(body: PhysicsBody): BodyBounds | null {
        const pos = this.physics.getPosition(body.handle);
        const angle = this.physics.getRotation(body.handle);
        
        if (!pos || angle === null) return null;

        const { userData } = body;
        let halfW: number;
        let halfH: number;

        // Calculate base half-dimensions based on shape type
        switch (userData.shapeType) {
            case 'box':
            case 'bag':
                halfW = (userData.width ?? 50) / 2;
                halfH = (userData.height ?? 50) / 2;
                break;
            case 'triangle':
                // For triangle, use radius as bounding circle
                const r = userData.radius ?? 30;
                halfW = r;
                halfH = r;
                break;
            default:
                halfW = 25;
                halfH = 25;
        }

        // Calculate rotated bounding box (AABB of rotated rectangle)
        const cos = Math.abs(Math.cos(angle));
        const sin = Math.abs(Math.sin(angle));
        const rotatedHalfW = halfW * cos + halfH * sin;
        const rotatedHalfH = halfW * sin + halfH * cos;

        // Calculate area (use original dimensions, not rotated AABB)
        let area: number;
        if (userData.shapeType === 'triangle') {
            // Equilateral triangle area: (sqrt(3) / 4) * side^2
            // For radius r, side = r * sqrt(3), so area = (3 * sqrt(3) / 4) * r^2
            const r = userData.radius ?? 30;
            area = (3 * Math.sqrt(3) / 4) * r * r;
        } else {
            area = (userData.width ?? 50) * (userData.height ?? 50);
        }

        return {
            body,
            x: pos.x,
            y: pos.y,
            minX: pos.x - rotatedHalfW,
            maxX: pos.x + rotatedHalfW,
            minY: pos.y - rotatedHalfH,  // Top
            maxY: pos.y + rotatedHalfH,  // Bottom
            width: rotatedHalfW * 2,
            height: rotatedHalfH * 2,
            area,
        };
    }

    /**
     * Get bounds for all bodies in container.
     */
    getContainerBodyBounds(): BodyBounds[] {
        const bodies = this.getBodiesInContainer();
        const bounds: BodyBounds[] = [];

        for (const body of bodies) {
            const b = this.getBodyBounds(body);
            if (b) bounds.push(b);
        }

        return bounds;
    }

    /**
     * Calculate the "effective height" of the stack.
     * This is the Y position of the top of the stack (lowest minY value).
     */
    getStackTopY(): number {
        if (!this.geometry) return 0;

        const bounds = this.getContainerBodyBounds();
        if (bounds.length === 0) return this.geometry.floorY;

        let topY = this.geometry.floorY;
        for (const b of bounds) {
            if (b.minY < topY) {
                topY = b.minY;
            }
        }

        return topY;
    }

    /**
     * Calculate container volume up to a given height.
     * 
     * @param topY The Y coordinate of the top of the measured region
     * @returns Volume in pixels squared
     */
    getVolumeUpTo(topY: number): number {
        if (!this.geometry) return 0;

        const height = this.geometry.floorY - topY;
        if (height <= 0) return 0;

        return this.geometry.width * height;
    }

    /**
     * Clean up physics bodies.
     */
    destroy(): void {
        if (this.floorHandle !== null) {
            this.physics.removeStaticBody(this.floorHandle);
            this.floorHandle = null;
        }
        if (this.leftWallHandle !== null) {
            this.physics.removeStaticBody(this.leftWallHandle);
            this.leftWallHandle = null;
        }
        if (this.rightWallHandle !== null) {
            this.physics.removeStaticBody(this.rightWallHandle);
            this.rightWallHandle = null;
        }
        this.geometry = null;
    }
}
